import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Dashboard as TemplatesIcon, TableChart as DatasetsIcon } from '@mui/icons-material';
import InlineAlert from '../../../../shared/components/InlineAlert';
import { DesktopRecommendedArea } from '../../../../shared/components/DesktopRecommendedNotice';
import SnackbarAlert from '../../../../shared/components/SnackbarAlert';
import { useSnackbar } from '../../../../shared/hooks/useSnackbar';
import EmptyStateMessage from '../../../../shared/components/EmptyStateMessage';
import { EntityListPanel } from '../../../../shared/components/EntityListPanel';
import type { EntityListItemConfig } from '../../../../shared/components/EntityListPanel';
import DatasetStateBadge from './DatasetStateBadge';
import DatasetStatusPanel from './DatasetStatusPanel';
import DatasetEditor from './DatasetEditor';
import DatasetTemplatesPanel from './DatasetTemplatesPanel';
import type { RowEventTarget } from '../../../../types/frontend';
import { useDatasetWriteAccess, useEventDatasetStatus, useEventDatasets } from '../../hooks/useEventDatasets';
import type { DatasetSeed, EditorMode } from '../../utils/datasetFormUtils';
import type { EventDataset, EventDatasetRecord } from '../../../../types/backend';

interface DatasetsTabContentProps {
  customerId: string;
  /** Named in the delete confirmation beside the dataset. */
  customerName: string;
  onCountChange: (count: number) => void;
  /** An Explorer event to start a new dataset from; the tab opens with the editor on it. */
  seed?: DatasetSeed | null;
  /** Called once the seed has opened the editor, so a later visit to the tab starts from the list. */
  onSeedTaken?: () => void;
  /** Opens a dataset row's source event in the Event Explorer. */
  onOpenEvent?: (target: RowEventTarget) => void;
}

interface EditorState {
  mode: EditorMode;
  initial: EventDataset | null;
  seed: DatasetSeed | null;
  /** The definition it starts from came from a dataset template. */
  fromTemplate: boolean;
  /** A fresh key remounts the editor, so "create instead" starts from its own form. */
  key: number;
}

const DatasetRowBadge: React.FC<{ customerId: string; name: string }> = ({ customerId, name }) => {
  const { status, loading, error } = useEventDatasetStatus(customerId, name);
  return <DatasetStateBadge state={status?.state} loading={loading} error={error} />;
};

const DatasetsTabContent: React.FC<DatasetsTabContentProps> = ({
  customerId,
  customerName,
  onCountChange,
  seed,
  onSeedTaken,
  onOpenEvent,
}) => {
  const { datasets, loading, error } = useEventDatasets(customerId);
  const canEdit = useDatasetWriteAccess();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(
    () => (seed ? { mode: 'create', initial: null, seed, fromTemplate: false, key: 1 } : null),
  );
  const [browsingTemplates, setBrowsingTemplates] = useState(false);

  useEffect(() => {
    onCountChange(datasets.length);
  }, [datasets.length, onCountChange]);

  useEffect(() => {
    if (seed) onSeedTaken?.();
  }, [seed, onSeedTaken]);

  const openEditor = (mode: EditorMode, initial: EventDataset | null, fromTemplate = false) =>
    setEditor((prev) => ({ mode, initial, seed: null, fromTemplate, key: (prev?.key ?? 0) + 1 }));

  // A stable config keeps the memoised list rows, and the status read each badge makes, from rerendering on every tab render.
  const listConfig = useMemo<EntityListItemConfig<EventDatasetRecord>>(() => ({
    getPrimaryText: (d) => d.name,
    getSecondaryText: (d) => `${d.retention_days} days retention`,
    getId: (d) => d.name,
    getSearchText: (d) => d.name,
    getBadge: (d) => <DatasetRowBadge customerId={customerId} name={d.name} />,
    getTestId: (d) => `dataset-row-${d.name}`,
  }), [customerId]);

  const selected = datasets.find((d) => d.name === selectedName) ?? null;

  let detail: React.ReactNode;
  if (editor) {
    detail = (
      <DatasetEditor
        key={editor.key}
        customerId={customerId}
        mode={editor.mode}
        initial={editor.initial}
        sampleEvent={editor.seed?.sampleEvent}
        startPattern={editor.seed?.eventPattern}
        fromTemplate={editor.fromTemplate}
        onSaved={(record) => {
          showSnackbar(editor.mode === 'edit' ? 'Dataset updated' : 'Dataset created');
          setEditor(null);
          setSelectedName(record.name);
        }}
        onCreateInstead={(definition) => openEditor('create', definition)}
        onSaveFailed={(message) => showSnackbar(message, 'error')}
        onDiscard={() => setEditor(null)}
      />
    );
  } else if (loading) {
    detail = (
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress aria-label="Reading datasets" />
      </Box>
    );
  } else if (selected) {
    detail = (
      <DatasetStatusPanel
        key={selected.name}
        customerId={customerId}
        customerName={customerName}
        dataset={selected}
        onEdit={canEdit ? () => openEditor('edit', selected) : undefined}
        notify={showSnackbar}
        onOpenEvent={onOpenEvent}
      />
    );
  } else {
    detail = (
      <EmptyStateMessage
        icon={DatasetsIcon}
        title="Select a dataset to see its status"
        description="Choose a dataset from the list or create a new one"
      />
    );
  }

  if (browsingTemplates) {
    return (
      <DesktopRecommendedArea>
        <DatasetTemplatesPanel
          customerId={customerId}
          onUse={(definition) => {
            setBrowsingTemplates(false);
            // The template's definition is copied into a new dataset of this customer; nothing is saved yet.
            openEditor('create', { ...definition, customer_id: customerId }, true);
          }}
          onBack={() => setBrowsingTemplates(false)}
          notify={showSnackbar}
        />
        <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
      </DesktopRecommendedArea>
    );
  }

  return (
    <DesktopRecommendedArea>
      <Box sx={{ width: 320, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <EntityListPanel
          entities={datasets}
          selectedEntity={selected}
          isNew={editor !== null && editor.mode === 'create'}
          saving={false}
          config={listConfig}
          addButtonText="New Dataset"
          emptyStateText={error ? 'Datasets could not be read' : 'No datasets'}
          emptyStateHelperText={error ? '' : 'This customer has no Event Datasets defined'}
          onSelect={(d) => {
            setEditor(null);
            setSelectedName(d.name);
          }}
          onAddNew={canEdit ? () => openEditor('create', null) : undefined}
          headerContent={
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<TemplatesIcon />}
              onClick={() => setBrowsingTemplates(true)}
            >
              Start from template
            </Button>
          }
          isLoading={loading}
          searchable
        />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {error && (
          <InlineAlert severity="error" sx={{ mx: 3, mt: 2 }}>
            Could not read the datasets for this customer: {error}
          </InlineAlert>
        )}
        {detail}
      </Box>
      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
    </DesktopRecommendedArea>
  );
};

export default DatasetsTabContent;
